import React, { useState } from 'react';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';

type Props = {
  /** Called after Stripe confirms payment (may be async — e.g. server sync before redirect). */
  onSuccess: () => void | Promise<void>;
  /** Total to pay (e.g. "USD 45.00") — shown once on the left above the card form. Order ID stays in the summary column. */
  totalDueFormatted?: string;
  /** Optional ETA (days) to show in compact mobile info block. */
  estimatedDeliveryDays?: number | null;
};

export default function CheckoutPaymentForm({ onSuccess, totalDueFormatted, estimatedDeliveryDays }: Props) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setMessage(null);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: `${window.location.origin}/account/orders`,
        },
      });
      if (error) {
        setMessage(error.message || t('storefront.checkout_pay_error'));
        return;
      }
      if (paymentIntent?.status === 'succeeded') {
        await Promise.resolve(onSuccess());
      }
    } catch {
      setMessage(t('storefront.checkout_pay_error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(ev) => void handlePay(ev)} className="space-y-4 pb-36 sm:pb-0">
      {totalDueFormatted ? (
        <div className="hidden rounded-xl border border-emerald-200 bg-emerald-50/90 px-4 py-3 text-center sm:block">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-900/75">
            {t('storefront.checkout_total_due')}
          </div>
          <div className="mt-0.5 text-xl font-bold text-slate-900">{totalDueFormatted}</div>
        </div>
      ) : null}
      <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3 sm:p-4">
        <PaymentElement
          options={{
            layout: 'tabs',
          }}
        />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600 sm:hidden">
        <p>
          {t('storefront.checkout_payments_powered_by', { defaultValue: 'Payments powered by' })}{' '}
          <a
            href="https://stripe.com"
            target="_blank"
            rel="noreferrer"
            className="font-semibold underline"
          >
            Stripe
          </a>
          .
        </p>
        <p className="mt-1">
          {t('storefront.checkout_pay_subtitle', {
            defaultValue:
              'Visa, Mastercard, American Express and other major cards via Stripe. You can finish later from your orders if you leave this page.',
          })}
        </p>
        {estimatedDeliveryDays != null && Number.isFinite(Number(estimatedDeliveryDays)) ? (
          <p className="mt-1">
            {t('storefront.delivery_eta')}: ~
            {t('account.eta_days', {
              days: Math.round(Number(estimatedDeliveryDays)),
            })}
          </p>
        ) : null}
      </div>
      {message ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {message}
        </div>
      ) : null}
      <button
        type="submit"
        disabled={!stripe || busy}
        className="hidden w-full rounded-full bg-[#00b853] px-6 py-3 text-sm font-semibold text-white hover:bg-[#009946] disabled:cursor-not-allowed disabled:opacity-60 sm:block"
      >
        {busy ? t('storefront.checkout_pay_processing') : t('storefront.checkout_pay_submit')}
      </button>
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-2 backdrop-blur sm:hidden">
        <div className="mx-auto max-w-5xl">
          {totalDueFormatted ? (
            <div className="mb-2 flex items-center justify-between text-sm">
              <span className="font-medium text-slate-600">{t('storefront.checkout_total_due')}</span>
              <span className="text-xl font-bold text-slate-900">{totalDueFormatted}</span>
            </div>
          ) : null}
          <button
            type="submit"
            disabled={!stripe || busy}
            className="w-full rounded-full bg-[#00b853] px-6 py-3 text-sm font-semibold text-white hover:bg-[#009946] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? t('storefront.checkout_pay_processing') : t('storefront.checkout_pay_submit')}
          </button>
        </div>
      </div>
    </form>
  );
}
